import * as React from 'react';
import {classify} from 'src/utils/classify';

import css from 'src/designSystem2021Components/banner/banner.css';
import {BannerCard} from './banner-card/banner-card';
import type {ContentWarningMessageDetails} from 'src/types/messages';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  SubTitleExtraSmall,
  ButtonTextSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import useI18n from 'src/hooks/useI18n';


export type BannerProps = {
  data: Array<ContentWarningMessageDetails>;
  handleResolve: (idx: number, warning_type: string) => Promise<void>;
  handleClose: (idx: number, warning_type: string) => void;
  onMouseEnter: (warning_type: string) => void;
  onMouseLeave: (warning_type: string) => void;
  className?: string;
  linkToMoreInfo?: string;
  segmentNode?: React.ReactNode;
};

export const Banner = ({
  data,
  className,
  handleResolve,
  handleClose,
  onMouseEnter,
  onMouseLeave,
  linkToMoreInfo,
  segmentNode,
}: BannerProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [showAll, setShowAll] = React.useState(false);
  const [visibleData, setVisibleData] = React.useState<
    ContentWarningMessageDetails[]
  >([]);
  const visibleBannersLength = data?.length + (segmentNode ? 1 : 0);
  const [warningsCount, setWarningsCount] =
    React.useState(visibleBannersLength);
  const setVisibleDataBasedOnShowAll = (value: boolean) => {
    if (value) {
      setVisibleData(data || []);
    } else if (segmentNode) {
      // if segmentNode is present, it should just show the segmentNode
      setVisibleData([]);
    } else {
      setVisibleData(data && data?.[0] ? [data?.[0]] : []);
    }
  };
  const handleClick = () => {
    const value = !showAll;
    setShowAll((prevValue) => !prevValue);
    setVisibleDataBasedOnShowAll(value);
  };
  React.useEffect(() => {
    setShowAll(showAll);
    setVisibleDataBasedOnShowAll(showAll);
    setWarningsCount(visibleBannersLength);
  }, [data, showAll, warningsCount, segmentNode]);

  if (data.length === 0 && !segmentNode) {
    return null;
  }

  return (
    <div className={classify(css.bannerContainer, className)}>
      <div className={css.bannerBox}>
        {warningsCount > 1 && (
          <button className={css.bannerShowButton} onClick={handleClick}>
            <ButtonTextSmall color="primary">
              {showAll
                ? t('Show_Less', 'Show Less')
                : `${t('Show_All', 'Show All')} (${warningsCount})`}
            </ButtonTextSmall>
            <Icon
              color="primary"
              name={showAll ? 'chevron-up' : 'chevron-down'}
              size="small"
              type="regular"
            />
          </button>
        )}
        <div className={css.bannerCardsContainer}>
          {segmentNode && <div className={css.segmentNode}>{segmentNode}</div>}
          {data && visibleData
            ? visibleData.map((bannerData, idx) => (
                <BannerCard
                  warningType={bannerData.warning_type}
                  bannerText={bannerData.warning_message}
                  showResolveButton={bannerData.show_resolve_button}
                  handleClose={() => {
                    handleClose(idx, bannerData.warning_type);
                    setWarningsCount((prevValue) => prevValue - 1);
                  }}
                  onMouseEnter={() => {
                    onMouseEnter(bannerData.warning_type);
                  }}
                  onMouseLeave={() => {
                    onMouseLeave(bannerData.warning_type);
                  }}
                  handleResolve={async () => {
                    handleResolve(idx, bannerData.warning_type);
                    setWarningsCount((prevValue) => prevValue - 1);
                  }}
                />
              ))
            : null}
        </div>
        <a href={linkToMoreInfo} target="_blank">
          <div className={css.bannerBottomMessageContainer}>
            <SubTitleExtraSmall color="secondary">
              {t('Message_Quality_Suggestion', 'Message Quality Suggestion')}
            </SubTitleExtraSmall>
            <Icon
              color="secondary"
              name="circle-info"
              size="small"
              type="regular"
            />
          </div>
        </a>
      </div>
    </div>
  );
};
